/**
 * PATH: packages/ui/src/kanban/components/KanbanBoardSwitcherFlyout.tsx
 * FILE_ID: UI-KANBAN-BOARD-SWITCHER-FLYOUT-001
 * PURPOSE: Portalled category → boards cascade panel for the Boards switcher.
 * WHY: Nested absolute fly-outs inside the primary menu's overflow-y-auto
 *   were clipped (BUG-2026-07-29-KANBAN-BOARD-SWITCHER-SUBMENU-CLIP).
 * FLOW: placement + rows → fixed portal on document.body
 */
import { forwardRef } from 'react'
import { createPortal } from 'react-dom'

import { boardKindLabel } from './KanbanBoardList'
import type { BoardKind } from '../types'
import type { ParentContextRow } from '../utils/boardHierarchy'

export type SwitcherFlyoutPlacement = {
  left: number | null
  right: number | null
  top: number
  maxHeight: number
}

export interface KanbanBoardSwitcherFlyoutProps {
  kind: BoardKind
  rows: ParentContextRow[]
  placement: SwitcherFlyoutPlacement
  currentBoardId?: string | null
  onSelect: (boardId: string) => void
}

export const KanbanBoardSwitcherFlyout = forwardRef<
  HTMLDivElement,
  KanbanBoardSwitcherFlyoutProps
>(function KanbanBoardSwitcherFlyout(
  { kind, rows, placement, currentBoardId, onSelect },
  ref,
) {
  if (typeof document === 'undefined') return null
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={`${boardKindLabel(kind)} boards`}
      data-testid="kanban-board-switcher-flyout"
      style={{
        position: 'fixed',
        top: placement.top,
        left: placement.left ?? undefined,
        right: placement.right ?? undefined,
        maxHeight: placement.maxHeight,
        zIndex: 10000,
      }}
      className="w-60 overflow-y-auto overscroll-contain rounded-md border border-neutral-200 bg-white py-1 shadow-xl dark:border-zinc-800 dark:bg-zinc-950"
    >
      {rows.map(({ board: b, nested, parentName }) => {
        const isCurrent = b.id === currentBoardId
        return (
          <button
            key={b.id}
            type="button"
            role="menuitem"
            onClick={() => onSelect(b.id)}
            data-testid="kanban-board-switcher-board"
            className={[
              'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs',
              nested ? 'pl-6' : '',
              isCurrent
                ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300'
                : 'text-neutral-700 hover:bg-neutral-100 dark:text-zinc-200 dark:hover:bg-zinc-900',
            ].join(' ')}
          >
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1 truncate">
                {nested && (
                  <span aria-hidden className="shrink-0 text-neutral-400">
                    ↳
                  </span>
                )}
                <span className="truncate">{b.name}</span>
              </span>
              {parentName && (
                <span className="block truncate text-[10px] text-neutral-400 dark:text-neutral-500">
                  in {parentName}
                </span>
              )}
            </span>
            {isCurrent && (
              <span className="shrink-0 text-[10px] uppercase tracking-wide text-blue-400">
                current
              </span>
            )}
          </button>
        )
      })}
    </div>,
    document.body,
  )
})
